import React from 'react';

interface YoutubeVideoProps {
  /**
   * URL đầy đủ của video YouTube hoặc video ID
   */
  videoUrl?: string;
  
  /**
   * Video ID (nếu không truyền videoUrl)
   */
  videoId?: string;
  
  /**
   * Tiêu đề của video (cho accessibility)
   */
  title?: string;
  
  /**
   * Chiều cao của video (mặc định là auto với aspect ratio 16:9)
   */
  height?: string | number;
  
  /**
   * Chiều rộng của video (mặc định là 100%)
   */
  width?: string | number;
  
  /**
   * CSS class tùy chỉnh cho container
   */
  className?: string;
  
  /**
   * Tự động phát video khi load (default: false)
   */
  autoplay?: boolean;
  
  /**
   * Hiển thị controls (default: true)
   */
  controls?: boolean;
  
  /**
   * Chế độ loop video (default: false)
   */
  loop?: boolean;
  
  /**
   * Tắt tiếng khi autoplay (default: true nếu autoplay)
   */
  muted?: boolean;
}

const YoutubeVideo: React.FC<YoutubeVideoProps> = ({
  videoUrl,
  videoId,
  title = 'YouTube video player',
  height,
  width = '100%',
  className = '',
  autoplay = false,
  controls = true,
  loop = false,
  muted = true
}) => {
  // Extract video ID from URL if provided
  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([^&\n?#]+)$/ // Direct ID
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };
  
  // Get the final video ID
  const finalVideoId = videoId || (videoUrl ? extractVideoId(videoUrl) : null);
  
  if (!finalVideoId) {
    return (
      <div className={`bg-gray-200 rounded-lg p-8 text-center ${className}`}>
        <p className="text-gray-600">Video không khả dụng</p>
      </div>
    );
  }
  
  // Build embed URL with parameters
  const embedParams = new URLSearchParams({
    ...(autoplay && { autoplay: '1' }),
    ...(controls === false && { controls: '0' }),
    ...(loop && { loop: '1', playlist: finalVideoId }),
    ...(muted && { mute: '1' }),
    rel: '0', // Don't show related videos from other channels
    modestbranding: '1' // Minimal YouTube branding
  });
  
  const embedUrl = `https://www.youtube.com/embed/${finalVideoId}?${embedParams.toString()}`;
  
  return (
    <div 
      className={`relative w-full overflow-hidden rounded-lg shadow-lg bg-black ${className}`}
      style={{ 
        paddingBottom: height ? 0 : '56.25%', // 16:9 aspect ratio if no height specified
        height: height || undefined
      }}
    >
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={embedUrl}
        title={title}
        width={typeof width === 'number' ? width : undefined}
        height={typeof height === 'number' ? height : undefined}
        style={{
          width: typeof width === 'string' ? width : undefined,
          height: typeof height === 'string' ? height : undefined,
        }}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

export default YoutubeVideo;
